<script setup>
import config from "../config";
import MPSetup from '../components/MPSetup.vue';
import { computed, ref } from "vue";
import { useI18n } from 'vue-i18n';
import { copyText } from '../services/clipboard';
import { miningPassword } from '../services/aux';
import { mdiInformationVariantCircleOutline, mdiContentCopy, mdiCheckCircleOutline } from '@mdi/js'
import euIcon from "../assets/locale/eu.svg";
import ruIcon from "../assets/locale/ru.svg";

let snackbar = ref(false);
const copyFailed = ref(false);
const { t } = useI18n();
const wecID = ref('');
const workerExample = computed(() => `${t('help_address_placeholder')}.worker1`);

const endpoints = [
    { label: 'EU', host: 'eu', image: euIcon },
    { label: 'RU', host: 'ru', image: ruIcon },
];
const poolHost = config.ports[0]?.host ?? '';
const difficultyColors = {
    LOW: 'teal',
    MID: 'indigo',
    HIGH: 'deep-orange',
};
const difficultyBorderColors = {
    LOW: '#009688',
    MID: '#3f51b5',
    HIGH: '#ff5722',
};
const selectedEndpoint = ref(endpoints[0]);
const selectedPort = ref(config.ports[0] ?? null);
const customDifficultyPort = config.ports.find(port => port.customDifficulty);
const useCustomDifficulty = ref(false);
const customDifficulty = ref(String((customDifficultyPort?.diff ?? 1024) * 2));
const validCustomDifficulty = computed(() => {
    const value = String(customDifficulty.value ?? '').trim();
    return /^[0-9]+(?:\.[0-9]{1,8})?$/.test(value) && Number.isFinite(Number(value)) && Number(value) > 0;
});
const passwordExample = computed(() => miningPassword(wecID.value,
    useCustomDifficulty.value ? String(customDifficulty.value).trim() : undefined));
const selectedExtraNonce = ref(
    config.extraNonce.profiles.find(profile => profile.size === config.extraNonce.default)
);
const standardProfiles = config.extraNonce.profiles.filter(profile => profile.standard);
const compatibilityProfiles = config.extraNonce.profiles.filter(profile => !profile.standard);
const extraNonceGuide = [
    { miner: 'extranonce_most_miners', recommendation: 'extranonce_standard_recommendation' },
    { miner: 'extranonce_nicehash', recommendation: 'extranonce_standard_recommendation' },
    { miner: 'extranonce_z9', recommendation: 'extranonce_standard_recommendation' },
    { miner: 'extranonce_z11', recommendation: 'extranonce_standard_first' },
    { miner: 'extranonce_z15', recommendation: 'extranonce_standard_recommendation' },
    { miner: 'extranonce_z15_pro_2023', recommendation: 'extranonce_four_bytes' },
    { miner: 'extranonce_z15_pro_fr19', recommendation: 'extranonce_eight_bytes' },
    { miner: 'extranonce_z15_pro_unknown', recommendation: 'extranonce_try_profiles' },
    { miner: 'extranonce_a9', recommendation: 'extranonce_standard_recommendation' },
    { miner: 'extranonce_mrr', recommendation: 'extranonce_standard_recommendation' },
];

function selectPort(port) {
    selectedPort.value = port;
    if (!port.customDifficulty) useCustomDifficulty.value = false;
}

function setCustomDifficulty(enabled) {
    useCustomDifficulty.value = enabled;
    if (enabled && customDifficultyPort) selectedPort.value = customDifficultyPort;
}

function selectEndpoint(endpoint) {
    selectedEndpoint.value = endpoint;
}

function connectionHost() {
    return `${selectedEndpoint.value.host}${selectedExtraNonce.value?.suffix ?? ''}.${poolHost}`;
}

function connectionUrl() {
    return selectedPort.value
        ? `stratum+tcp://${connectionHost()}:${selectedPort.value.port}`
        : '';
}

function difficultyColor(port) {
    return difficultyColors[port.info] ?? 'primary';
}

function difficultyBorderColor(port) {
    return difficultyBorderColors[port.info] ?? 'currentColor';
}

async function copy(value) {
    copyFailed.value = !await copyText(value);
    snackbar.value = true;
}
</script>
<template>
    <v-snackbar v-model="snackbar" location="top" :color="copyFailed ? 'warning' : 'success'" min-width="150" min-height="30" :timeout="2000"
        class="pa-0">
        <v-icon v-if="!copyFailed" left size="22" :icon="mdiCheckCircleOutline"></v-icon>
        {{ $t(copyFailed ? 'help_copy_failed' : 'copy_successful') }}
    </v-snackbar>
    <div class="help-page">
    <h1 class="text-h6 mb-3">{{ $t('help_title') }}</h1>
    <v-row dense>
        <v-col cols="12" class="mb-4">
            <MPSetup @identity-change="wecID = $event" />
        </v-col>
        <v-col cols="12" class="mb-4">
            <h2 class="help-step-heading"><span class="help-step-number">1</span>{{ $t('help_choose_connection') }}</h2>
            <p class="text-body-2 text-medium-emphasis mb-3">{{ $t('help_connection_description') }}</p>
            <v-card class="location-card" variant="outlined">
                <v-card-text>
                    <div class="connection-steps">
                        <section class="connection-step">
                            <div class="connection-step-title">
                                <span>{{ $t('help_server_region') }}</span>
                            </div>
                                <div class="selected-location-options mb-4">
                                    <v-btn v-for="endpoint in endpoints" :key="endpoint.host" size="x-small"
                                        class="location-button" rounded="md" height="30"
                                        :variant="selectedEndpoint.host === endpoint.host ? 'tonal' : 'outlined'"
                                        :color="selectedEndpoint.host === endpoint.host ? 'primary' : undefined"
                                        :aria-pressed="selectedEndpoint.host === endpoint.host"
                                        @click="selectEndpoint(endpoint)">
                                        <img v-if="endpoint.image" class="location-icon mr-2"
                                            :src="endpoint.image" alt="" />
                                        <v-icon v-else class="mr-2" size="18" :icon="endpoint.icon"></v-icon>
                                        <span>{{ endpoint.label }}</span>
                                    </v-btn>
                                </div>
                            <div class="text-body-2 mb-2">{{ $t('choose_difficulty') }}</div>
                            <div class="difficulty-options mb-3">
                                <v-btn v-for="item in config.ports" :key="item.port" size="small"
                                    class="difficulty-button" rounded="md" height="36"
                                    :aria-pressed="selectedPort.port === item.port"
                                    :variant="selectedPort.port === item.port ? 'tonal' : 'outlined'"
                                    :color="difficultyColor(item)" @click="selectPort(item)">
                                    <span>{{ $t(item.info + "_BUTTON") }}</span>
                                    <span class="ml-1 text-caption">· <span v-html="$filters.difficulty(item.diff, 0)"></span></span>
                                </v-btn>
                            </div>
                            <div class="selected-mode" :style="{ borderColor: difficultyBorderColor(selectedPort) }">
                                <div class="text-body-2 py-2">
                                    {{ $t(selectedPort.info) }}
                                </div>
                            </div>
                            <div class="miner-compatibility mt-4">
                                <div class="text-body-2 mb-2">{{ $t("miner_compatibility") }}</div>
                                <div v-for="profile in standardProfiles" :key="profile.size"
                                    class="d-flex align-center flex-wrap ga-2 mb-2">
                                    <v-btn size="small" rounded="md" height="34" class="extranonce-button"
                                        :variant="selectedExtraNonce.size === profile.size ? 'tonal' : 'outlined'"
                                        :color="selectedExtraNonce.size === profile.size ? 'primary' : undefined"
                                        :aria-pressed="selectedExtraNonce.size === profile.size"
                                        @click="selectedExtraNonce = profile">
                                        {{ $t("standard") }} · {{ profile.size }} B
                                    </v-btn>
                                    <span v-if="profile.recommended" class="text-caption text-primary">
                                        {{ $t("recommended") }}
                                    </span>
                                </div>
                                <div class="d-flex align-center flex-wrap ga-2">
                                    <span class="text-caption text-medium-emphasis">{{ $t("compatibility") }}</span>
                                    <v-btn v-for="profile in compatibilityProfiles" :key="profile.size"
                                        size="small" rounded="md" height="30" class="extranonce-button"
                                        :variant="selectedExtraNonce.size === profile.size ? 'tonal' : 'outlined'"
                                        :color="selectedExtraNonce.size === profile.size ? 'primary' : undefined"
                                        :aria-pressed="selectedExtraNonce.size === profile.size"
                                        :aria-label="$t('extranonce_profile_label', { size: profile.size })"
                                        @click="selectedExtraNonce = profile">
                                        {{ profile.size }} B
                                    </v-btn>
                                </div>
                                <div class="text-caption text-medium-emphasis mt-2">
                                    ExtraNonce1: {{ selectedExtraNonce.size }} B
                                    <span v-if="selectedExtraNonce.standard">
                                        · {{ $t("extranonce_standard_description") }}
                                    </span>
                                </div>
                                <v-expansion-panels variant="accordion" class="extranonce-help mt-3">
                                    <v-expansion-panel elevation="0" bg-color="transparent">
                                        <v-expansion-panel-title class="text-body-2 px-0" min-height="40">
                                            {{ $t("extranonce_which_mode") }}
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <p class="text-caption mb-2">{{ $t("extranonce_help") }}</p>
                                            <p class="text-caption text-medium-emphasis mb-3">
                                                {{ $t("extranonce_fallback_description") }}
                                            </p>
                                            <table class="extranonce-guide text-caption">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">{{ $t("extranonce_miner_service") }}</th>
                                                        <th scope="col">{{ $t("recommended") }}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in extraNonceGuide" :key="row.miner">
                                                        <td>{{ $t(row.miner) }}</td>
                                                        <td>{{ $t(row.recommendation) }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                                <div class="d-flex align-start ga-2 mt-2 text-medium-emphasis">
                                    <v-icon size="16" class="mt-1" :icon="mdiInformationVariantCircleOutline"></v-icon>
                                    <span class="text-caption">{{ $t("extranonce_proxy_note") }}</span>
                                </div>
                            </div>
                        </section>

                        <section class="connection-step">
                            <div class="connection-step-title">
                                <span>{{ $t("copy_connection") }}</span>
                            </div>
                            <div class="text-caption text-medium-emphasis mb-2">
                                {{ selectedEndpoint.label }} · {{ $t(selectedPort.info + "_BUTTON") }}
                                · {{ $t(selectedExtraNonce.standard ? 'standard' : 'compatibility') }}
                                {{ selectedExtraNonce.size }} B
                            </div>
                            <div class="d-flex align-center ga-1">
                                <v-code tag="div" class="connection-url flex-grow-1">
                                    {{ connectionUrl() }}
                                </v-code>
                                <v-btn variant="text" size="small" :ripple="false" icon
                                    :aria-label="$t('copy_connection')"
                                    @click="copy(connectionUrl())">
                                    <v-icon size="18" :icon="mdiContentCopy"></v-icon>
                                </v-btn>
                            </div>

                            <div class="hashrate-guide mt-4">
                                <div class="text-caption text-medium-emphasis mb-2">
                                    {{ $t("recommended_hashrate_guide") }}
                                </div>
                                <div class="hashrate-guide-current">
                                    <span>{{ $t(selectedPort.info + "_BUTTON") }}</span>
                                    <span class="text-medium-emphasis">
                                        {{ $t(selectedPort.info + "_HASHRATE") }}
                                    </span>
                                </div>
                                <div class="text-caption text-medium-emphasis mt-2">
                                    {{ $t("hashrate_guide_note") }}
                                </div>
                            </div>
                        </section>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
        <v-col cols="12">
            <h2 class="help-step-heading"><span class="help-step-number">2</span>{{ $t('help_enter_settings') }}</h2>
            <p class="text-body-2 text-medium-emphasis mb-3">{{ $t('help_settings_description') }}</p>
            <v-card class="configuration-panel" variant="outlined">
                <v-card-text>
                    <div class="connection-steps">
                        <section class="connection-step">
                            <h3 class="text-subtitle-2 mb-3">{{ $t('help_zec_settings') }}</h3>
                            <div class="configuration-step">
                                <div class="mb-1">URL / Pool</div>
                                <p class="text-body-2 text-medium-emphasis">{{ $t('help_url_description') }}</p>
                            </div>
                            <div class="configuration-step">
                                <div class="mb-1">Worker / Username</div>
                                <p class="text-body-2 text-medium-emphasis">{{ $t('help_direct_worker') }}</p>
                                <p class="text-body-2 text-medium-emphasis mt-2">{{ $t('help_worker_suffix') }}</p>
                            </div>
                            <div class="configuration-step">
                                <div class="mb-1">Password</div>
                                <p class="text-body-2 text-medium-emphasis">{{ $t('help_password_description') }}</p>
                                <div v-if="customDifficultyPort" class="custom-difficulty mt-3">
                                    <label class="custom-difficulty-toggle">
                                        <input type="checkbox" :checked="useCustomDifficulty" @change="setCustomDifficulty($event.target.checked)" />
                                        {{ $t('help_custom_difficulty') }}
                                    </label>
                                    <div v-if="useCustomDifficulty" class="mt-2">
                                        <v-text-field v-model="customDifficulty" :label="$t('help_difficulty_value')" prefix="d=" inputmode="decimal" type="text" variant="filled" density="compact" hide-details="auto" :maxlength="64" :error-messages="validCustomDifficulty ? [] : [$t('help_difficulty_invalid')]" />
                                    </div>
                                    <p class="text-caption text-medium-emphasis mt-2">{{ $t('help_custom_difficulty_port', { port: customDifficultyPort.port }) }}</p>
                                </div>
                            </div>
                        </section>
                        <section class="connection-step" aria-live="polite">
                            <h3 class="text-subtitle-2 mb-3">{{ $t('connection_example') }}</h3>
                            <div class="configuration-preview">
                                <div class="configuration-preview-row">
                                    <span class="text-caption text-medium-emphasis">URL / Pool</span>
                                    <div class="d-flex align-center ga-1">
                                        <v-code tag="div" class="configuration-value flex-grow-1">{{ connectionUrl() }}</v-code>
                                        <v-btn variant="text" size="small" icon :aria-label="$t('copy_connection')" @click="copy(connectionUrl())"><v-icon size="18" :icon="mdiContentCopy" /></v-btn>
                                    </div>
                                </div>
                                <div class="configuration-preview-row">
                                    <span class="text-caption text-medium-emphasis">Worker / Username</span>
                                    <div class="d-flex align-center ga-1">
                                        <v-code tag="div" class="configuration-value flex-grow-1">{{ workerExample }}</v-code>
                                    </div>
                                    <p class="text-caption text-medium-emphasis">{{ $t('help_replace_address') }}</p>
                                </div>
                                <div class="configuration-preview-row">
                                    <span class="text-caption text-medium-emphasis">Password</span>
                                    <div class="d-flex align-center ga-1">
                                        <v-code tag="div" class="configuration-value flex-grow-1">{{ passwordExample || '—' }}</v-code>
                                        <v-btn variant="text" size="small" icon :disabled="!passwordExample" :aria-label="$t('help_copy_password')" @click="copy(passwordExample)"><v-icon size="18" :icon="mdiContentCopy" /></v-btn>
                                    </div>
                                </div>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mt-4">{{ $t('help_start_mining') }}</p>
                        </section>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
    </div>
</template>
<style scoped>
.help-step-heading { display: flex; align-items: center; gap: 10px; font-size: 18px; margin-bottom: 12px; }
.help-step-number { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 26px; height: 26px; border-radius: 50%; background: rgba(33, 150, 243, .15); color: #90caf9; font-size: 14px; }
.custom-difficulty { max-width: 360px; }
.custom-difficulty-toggle { display: flex; align-items: center; gap: 8px; font-size: 14px; cursor: pointer; }
.custom-difficulty-toggle input { width: 16px; height: 16px; accent-color: #2196f3; }
.custom-difficulty :deep(input), .custom-difficulty :deep(.v-field-label), .custom-difficulty :deep(.v-text-field__prefix) { font-size: 14px; }
.custom-difficulty :deep(.v-text-field--prefixed .v-field__input) { --v-field-padding-start: 0px; }
.miner-compatibility {
    padding-top: 12px;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.extranonce-button {
    min-width: 0;
    padding-inline: 10px;
    letter-spacing: normal;
}

.extranonce-help :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 0 8px;
}

.extranonce-guide {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
}

.extranonce-guide th,
.extranonce-guide td {
    padding: 6px 8px 6px 0;
    text-align: left;
    vertical-align: top;
    overflow-wrap: anywhere;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.connection-url {
    min-width: 0;
    overflow-wrap: anywhere;
}

.connection-steps {
    display: grid;
    grid-template-columns: minmax(520px, 1.6fr) minmax(320px, 1fr);
}

.connection-step {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0 16px;
}

.connection-step:first-child {
    padding-left: 0;
}

.connection-step:last-child {
    padding-right: 0;
}

.connection-step+.connection-step {
    border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.connection-step-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.difficulty-button,
.location-button {
    min-width: 0 !important;
    padding-inline: 8px !important;
    letter-spacing: normal !important;
}

.difficulty-button :deep(.v-btn__content),
.location-button :deep(.v-btn__content),
.location-card :deep(.v-card-title) {
    font-weight: 400 !important;
    text-transform: none !important;
}

.selected-location-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.location-icon {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    object-fit: cover;
}

.difficulty-options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.selected-mode {
    padding-left: 12px;
    border-left: 3px solid;
}

.hashrate-guide {
    padding-top: 12px;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.hashrate-guide-current {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 8px;
    border-radius: 6px;
    background: rgba(var(--v-theme-primary), .1);
}

.configuration-step+.configuration-step {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.configuration-value {
    min-width: 0;
    overflow-wrap: anywhere;
}

.configuration-preview {
    display: grid;
    gap: 16px;
}

.configuration-preview-row {
    display: grid;
    gap: 6px;
}

@media (max-width: 1050px) {
    .connection-steps {
        grid-template-columns: 1fr;
    }

    .connection-step {
        padding: 16px 0;
    }

    .connection-step:first-child {
        padding-top: 0;
    }

    .connection-step:last-child {
        padding-bottom: 0;
    }

    .connection-step+.connection-step {
        border-left: 0;
        border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
}

.v-code {
  font-size: 0.9rem !important;
}

</style>
